import { UNKNOWN, statedByUser, type Citation, type Provenance } from '@asvise/facts'
import type {
  Benefit,
  BenefitPool,
  Policy,
  PremiumFrequency,
} from '@asvise/household-engine/vocabulary'
import { READER_VERSION, type BenefitReading, type DocumentReading, type Reading } from './types.ts'

/**
 * A reading, once somebody has accepted it, as a policy the engine can compare.
 *
 * Two rules decide everything here, and both are about what may not be asserted.
 *
 * AN UNREAD VALUE IS UNKNOWN, NEVER A NEGATIVE. A benefit whose waiting period
 * nobody read has an unknown waiting period, not none. The engine treats
 * UNKNOWN as a reason to review and treats none as a fact, and the difference
 * between them is the difference between a household being told to check
 * something and being told it is fine.
 *
 * BENEFITS THAT SHARE A SUM GO IN A POOL. Where any benefit was read with a
 * pool total, every benefit on the policy that could draw on it is given the
 * same pool id. The engine then refuses to add them, refuses to report more
 * than the pool as available, and can say what paying one would leave the
 * others. Getting this wrong is the one error that overstates a family's
 * protection, which is the direction this must never fail in.
 */

const from = (at: string, citation: Citation | undefined): Provenance =>
  citation ? { source: 'DOCUMENT', observed_at: at, citation } : statedByUser(at)

const valueOf = <T>(r: Reading<T> | null): T | UnknownOr<T> => (r ? r.value : UNKNOWN)
type UnknownOr<T> = typeof UNKNOWN | T

/**
 * Which benefits can draw on a shared sum at all.
 *
 * Death, disability and critical illness do, because a policy that accelerates
 * pays one of them out of the others. A hospital reimbursement never does: it
 * pays a hospital for a bill and has no sum to share.
 */
const POOLABLE = new Set(['DEATH', 'TPD', 'CRITICAL_ILLNESS', 'LONG_TERM_CARE'])

const POOL_ID = 'BASE'

function poolFrom(readings: BenefitReading[], at: string): BenefitPool[] {
  const stated = readings.find((b) => b.pool_total !== null)
  if (!stated?.pool_total) return []

  const members = readings.filter((b) => POOLABLE.has(b.code)).map((b) => b.code)
  if (members.length < 2) return []

  return [
    {
      id: POOL_ID,
      initial_amount: stated.pool_total.value,
      // Not `style: 'currency'`, which renders SGD as a bare `$` here and is a
      // dollar a household could read as any other. The third place that has
      // been: the findings card and the demo script were the first two.
      note: `${members.map(readable).join(', ')} are claims against one sum of S$${stated.pool_total.value.toLocaleString('en-SG')}.`,
      from: from(at, stated.pool_total.citation),
    },
  ]
}

const readable = (code: string): string =>
  ({
    DEATH: 'death',
    TPD: 'permanent disability',
    CRITICAL_ILLNESS: 'critical illness',
    LONG_TERM_CARE: 'long-term care',
  })[code] ?? code.toLowerCase().replace(/_/g, ' ')

function benefitFrom(reading: BenefitReading, pooled: boolean, at: string): Benefit {
  const shares = pooled && POOLABLE.has(reading.code)

  return {
    code: reading.code,
    /**
     * Null until a person says who this is.
     *
     * The schedule's own words are kept beside it. Matching "Daniel Tan; male;
     * non-smoker at issue" to a member of this household is a judgement only
     * the household can make, and cover nobody has matched is excluded from
     * every total rather than guessed at.
     */
    insured_person_id: null,
    insured_as_printed: reading.insured_as_printed?.value ?? null,
    amount: reading.amount ? reading.amount.value : UNKNOWN,
    /**
     * A benefit in a pool is accelerated whatever it said about itself.
     *
     * The reader can only confirm an acceleration where the schedule uses the
     * word, and a schedule that states the pool once at the foot of the page
     * and never repeats it per row would otherwise produce three ADDITIONAL
     * benefits under a pool that caps them. The pool is the stronger evidence:
     * it is a statement that these cannot all be paid.
     */
    basis: shares ? 'ACCELERATED' : (reading.basis?.value ?? 'ADDITIONAL'),
    pool_id: shares ? POOL_ID : null,
    timing: {
      waiting_period_days: valueOf(reading.waiting_days),
      survival_period_days: valueOf(reading.survival_days),
      expires_at_age: valueOf(reading.expires_at_age),
      expires_on: valueOf(reading.expires_on),
      from: from(at, reading.expires_on?.citation ?? reading.found),
    },
    ...(reading.annual_limit || reading.deductible || reading.co_insurance
      ? {
          annual_limit: valueOf(reading.annual_limit),
          cost_sharing: {
            deductible: valueOf(reading.deductible),
            co_insurance: valueOf(reading.co_insurance),
            rider_reduces_cost_sharing: valueOf(reading.cost_sharing_rider),
            out_of_pocket_cap: valueOf(reading.out_of_pocket_cap),
            from: from(at, reading.deductible?.citation ?? reading.found),
          },
        }
      : {}),
    from: from(at, reading.amount?.citation ?? reading.found),
    ...(reading.found ? { citation: reading.found } : {}),
  }
}

export function toPolicy(
  reading: DocumentReading,
  at: string,
  /**
   * What the document-quality gate found against this file.
   *
   * Passed in rather than recomputed, because the gate reads pages and this
   * function reads a reading. The caller has both and has already shown the
   * findings to somebody.
   */
  review_required: string[] = [],
): Policy {
  const granted = reading.benefits.filter((b) => b.operative)
  const pools = poolFrom(granted, at)
  const pooled = pools.length > 0

  const warnings: string[] = []
  if (reading.beneficiary === null) {
    warnings.push(
      'No beneficiary is recorded on the schedule, so who would receive a death benefit has not been established.',
    )
  }
  if (reading.premium_guaranteed?.value === false) {
    warnings.push(
      'The premium rate is not guaranteed for the whole term, so what this policy costs can change.',
    )
  }
  if (reading.end_date === null && reading.premium_term_end !== null) {
    warnings.push(
      'The premiums stop before the cover does. The premium payment term is not the end of the cover.',
    )
  }
  for (const b of granted) {
    if (b.amount === null && b.annual_limit === null) {
      warnings.push(`No figure was read for the ${readable(b.code)} benefit.`)
    }
  }

  return {
    id: newId(),
    insurer: reading.insurer?.value ?? '',
    product_name: reading.product_name?.value ?? '',
    policy_number: reading.policy_number?.value ?? '',
    policyholder_person_id: null,
    policyholder_as_printed: reading.policyholder?.value ?? null,
    beneficiary_as_printed: reading.beneficiary ? reading.beneficiary.value : null,
    source: 'PERSONAL',
    /**
     * Portable unless it is employer cover, and nothing here can tell.
     *
     * A schedule does not say whether it came through a job. The questionnaire
     * asks, and the answer is stamped on the policy afterwards: an employer
     * benefit is treated as ending with the job unless evidence says otherwise,
     * and a default of `true` here would quietly make that evidence
     * unnecessary. So it is UNKNOWN, which the engine reads as a caveat.
     */
    portable: UNKNOWN,
    start_date: reading.start_date?.value ?? null,
    end_date: reading.end_date ? reading.end_date.value : null,
    premium_term_end: reading.premium_term_end ? reading.premium_term_end.value : UNKNOWN,
    premium: {
      amount: reading.premium_amount ? reading.premium_amount.value : UNKNOWN,
      currency: 'SGD',
      frequency: frequencyOf(reading.premium_frequency?.value ?? null),
      guaranteed: reading.premium_guaranteed ? reading.premium_guaranteed.value : UNKNOWN,
      from: from(at, reading.premium_amount?.citation),
    },
    pools,
    benefits: granted.map((b) => benefitFrom(b, pooled, at)),
    /**
     * Read from the schedule only, so usually empty.
     *
     * Exclusions live in the wording, which this reader never sees. An empty
     * list here is therefore not a claim that a policy excludes nothing, and
     * the engine must not read it as one: what it means is that nobody has
     * looked, which is a different thing and belongs in the warnings.
     */
    exclusions: [],
    review_required,
    read_by_version: READER_VERSION,
    /**
     * Null here, and set by the screen that asked somebody.
     *
     * This function turns a reading into a policy and knows nothing about
     * whether a person agreed with it. Defaulting it to the moment of
     * conversion would record every reading as confirmed, including the ones
     * produced by a test.
     */
    confirmed_at: null,
    extraction_warnings: [
      ...warnings,
      'Exclusions and definitions live in the policy wording, which this reading did not cover.',
    ],
  }
}

const FREQUENCIES: Record<string, PremiumFrequency> = {
  month: 'MONTHLY',
  quarter: 'QUARTERLY',
  half: 'HALF_YEARLY',
  year: 'YEARLY',
  annual: 'YEARLY',
  single: 'SINGLE',
}

function frequencyOf(said: string | null): PremiumFrequency {
  if (!said) return 'MONTHLY'
  const lower = said.toLowerCase()
  for (const [word, frequency] of Object.entries(FREQUENCIES)) {
    if (lower.includes(word)) return frequency
  }
  return 'MONTHLY'
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `pol-${Math.floor(Date.now() * Math.random())}`
}

/**
 * Nothing was read that anybody could review.
 *
 * The insurer is deliberately not part of this. It falls back to the running
 * head, which is on every page of every pack, so a reply that found nothing at
 * all still comes back with a real insurer name. Counting that as content would
 * offer somebody a policy card containing one line they cannot check and
 * nothing they can accept.
 */
export function isEmpty(reading: DocumentReading): boolean {
  return (
    reading.benefits.length === 0 &&
    reading.policy_number === null &&
    reading.premium_amount === null
  )
}
